import type { Campaign, CampaignProduct, Collab, CollabStatus } from '@foodfluence/shared';
import {
  campaignHasProductCatalog,
  getOptionalProducts,
  getRequiredProducts,
} from '@foodfluence/shared';
import {
  isBrandManagedCampaignType,
  isRetailCampaignType,
  isShopifyCampaignType,
} from 'utils/collabUtils';

export type SelectProductsDestination = 'shipping' | 'retail' | 'brandManaged' | 'shopify';

/**
 * Where a creator should go to settle their products, or `null` when the wizard has no part to
 * play.
 *
 * A campaign that carries a product catalog goes through the wizard, whether or not it offers a
 * choice: for shipping, Shopify and brand-managed the wizard IS the accept — it collects the
 * address, reviews the offer and ends on the confirmation — so a fixed offer loses nothing by
 * going through it and gains the same flow as every other campaign.
 *
 * Campaigns saved before the catalog existed have none, and keep the accept flow they have always
 * had, so no live campaign changes its rules under the brand that configured it.
 *
 * Retail included: the wizard collects its pickup location and posting date too, so it is the
 * accept there as well.
 */
export const productSelectionDestination = (
  campaign: Campaign | undefined | null
): SelectProductsDestination | null => {
  if (!campaign) return null;
  // The same predicate the server records a selection under — see `campaignHasProductCatalog`.
  const hasOffer = campaignHasProductCatalog(campaign);
  if (isShopifyCampaignType(campaign.campaignType)) return hasOffer ? 'shopify' : null;
  if (campaign.campaignType === 'shipToHome') return hasOffer ? 'shipping' : null;
  if (isBrandManagedCampaignType(campaign.campaignType)) return hasOffer ? 'brandManaged' : null;
  if (isRetailCampaignType(campaign.campaignType)) return hasOffer ? 'retail' : null;
  return null;
};

/**
 * The accept, and only the accept. A collab past it has an agreed product already — the invite-time
 * `productValue`/`productName` snapshot it was accepted on, which is also what its reserve was
 * taken against.
 *
 * `pendingRedemption` used to be here too, so that a catalog added to a live campaign collected a
 * choice at redeem. That asks the wrong question of the wrong creator: they agreed to a product
 * weeks ago, the brand's card was charged for it at acceptance, and the reserve cannot be raised
 * afterwards — `trueUpReserve` only ever releases. A creator who then picked the dearer of two
 * identically-named products left the collab under-reserved and the platform paying the
 * difference. The snapshot stands instead; the server no longer demands a selection at redeem.
 *
 * Shipping and Shopify are the exception — see `OWES_ACCEPT_AT_REDEMPTION`.
 */
const SELECTABLE_STATUSES: CollabStatus[] = ['pendingInfluencerApproval'];

/**
 * Destinations whose `pendingRedemption` is still BEFORE the creator's accept.
 *
 * A brand that approves a creator's request moves the collab straight to `pendingRedemption`
 * without an address or a product choice. For shipping and Shopify the creator's own accept is
 * what supplies both, and it moves the collab on to `pendingSubmission` — so one of these still at
 * `pendingRedemption` has never accepted, and its reserve sits at the campaign's ceiling, which the
 * pick then trues down. Retail is the opposite: its accept lands AT `pendingRedemption`, so there
 * the status does mean settled. Without this the brand-approved creator got the legacy picker,
 * which demands a variant for every product while the server refuses more than the allowance.
 */
const OWES_ACCEPT_AT_REDEMPTION: SelectProductsDestination[] = ['shipping', 'shopify'];

/**
 * Whether this collab still owes a product choice.
 *
 * Only while the accept can still land — see `SELECTABLE_STATUSES`. Past it, the collab carries
 * the product it was accepted on and owes nothing.
 */
export const needsProductSelection = (
  // `status` optional: an unknown status cannot rule the wizard out, and callers that only care
  // about the selection keep passing what they always did.
  collab: (Pick<Collab, 'selectedProducts'> & Partial<Pick<Collab, 'status'>>) | undefined | null,
  campaign: Campaign | undefined | null
): SelectProductsDestination | null => {
  // Status decides, first and on its own: a collab that has been accepted is in progress, and an
  // in-progress creator is never asked to pick a product. Not "has it recorded a selection" — a
  // collab accepted before its campaign had a catalog has none to show, and that absence is what
  // used to drag it into the wizard.
  const destination = productSelectionDestination(campaign);
  const owesAccept =
    !collab?.status ||
    SELECTABLE_STATUSES.includes(collab.status) ||
    (collab.status === 'pendingRedemption' && OWES_ACCEPT_AT_REDEMPTION.includes(destination));
  if (!owesAccept) return null;
  // Belt and braces for the accept itself, which is replayable: a collab that recorded a choice
  // on a first attempt must not be asked again on a retry.
  if (collab?.selectedProducts?.length) return null;
  return destination;
};

/**
 * What the creator actually receives, given what they picked.
 *
 * With a real choice that is the required products plus their picks. Without one — an allowance
 * that already covers every optional product — it is the WHOLE offer, including the optional ones:
 * nobody was asked to choose, so nothing may be dropped. An optional product left out here is a
 * product the brand promised and never ships.
 */
export const productsToReceive = (
  offeredProducts: CampaignProduct[],
  picked: string[],
  pickLimit: number
): CampaignProduct[] => {
  const optional = getOptionalProducts(offeredProducts);
  if (optional.length <= pickLimit) return offeredProducts;
  return [
    ...getRequiredProducts(offeredProducts),
    ...picked
      .map((id) => optional.find((product) => product.id === id))
      .filter((product): product is CampaignProduct => !!product),
  ];
};

/**
 * The picks after taking `productId`, honouring the allowance.
 *
 * At the limit the new pick DISPLACES the oldest rather than being refused. Making the creator
 * deselect something first is a dead end they have to work out for themselves, and the allowance
 * is a cap on what they receive, not on how many times they may change their mind.
 */
export const addPick = (
  picked: string[],
  productId: string,
  pickLimit: number
): string[] => {
  if (pickLimit <= 0) return picked;
  if (picked.includes(productId)) return picked;
  return [...picked, productId].slice(-pickLimit);
};
